import { useEffect, useState } from 'preact/hooks';
import { fetchMediaFile } from '../lib/api';
import './MediaEl.css';

// B5b: the OBJECT-URL media pattern, factored to one element each. Tenant scope
// rides the X-Tenant-Id header, which a bare <img>/<video> src can't carry — so
// each element fetches its own bytes with the header, wraps them in an object
// URL, and REVOKES that URL on unmount/refetch (no leaks, even for a grid of N
// looks each mounting its own <MediaImage>). Mirrors SourceViewer.tsx's cleanup
// discipline, but self-contained per element so a whole grid Just Works.
function useMediaObjectUrl(tenantId: string, id: string): { url: string | null; failed: boolean } {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    setUrl(null);
    setFailed(false);
    void fetchMediaFile(tenantId, id)
      .then((file) => {
        if (cancelled) {
          URL.revokeObjectURL(file.objectUrl);
          return;
        }
        revoke = file.objectUrl;
        setUrl(file.objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [tenantId, id]);

  return { url, failed };
}

export interface MediaImageProps {
  tenantId: string;
  id: string;
  alt: string;
  /** Extra class on the rendered <img> (the caller sizes/crops it). */
  className?: string;
}

/** A headshot/look as an <img>, loaded through the object-URL pattern. Renders
 * a calm placeholder while loading and on failure — never a broken-image icon,
 * never a layout break (max-width:100% lives in MediaEl.css). */
export function MediaImage({ tenantId, id, alt, className }: MediaImageProps) {
  const { url, failed } = useMediaObjectUrl(tenantId, id);
  const cls = `mediael__img${className ? ` ${className}` : ''}`;
  if (failed) return <div class="mediael mediael--failed" aria-label={`${alt} — unavailable`} role="img" />;
  if (!url) return <div class="mediael mediael--loading" aria-hidden="true" />;
  return <img class={cls} src={url} alt={alt} />;
}

export interface MediaVideoProps {
  tenantId: string;
  id: string;
  label: string;
}

/** A reel/clip as a native <video controls>, loaded through the object-URL
 * pattern. Same calm placeholder-on-load / graceful-on-failure contract. */
export function MediaVideo({ tenantId, id, label }: MediaVideoProps) {
  const { url, failed } = useMediaObjectUrl(tenantId, id);
  if (failed) return <div class="mediavid mediavid--failed">This clip couldn't be loaded.</div>;
  if (!url) return <div class="mediavid mediavid--loading" aria-hidden="true" />;
  // eslint-disable-next-line jsx-a11y/media-has-caption -- an actor's reel has no
  // caption track; the aria-label names it.
  return <video class="mediavid" src={url} controls preload="metadata" aria-label={label} />;
}
